/**
 * View rules for the Lanes tab layout (docs/ux/review/project-tab-refinement.md §2, WP-4):
 * zero-count collapse, the idle-lane fold, the one-count head sentence and the outdated-asks fold.
 * Pure: the model already has every number; these only decide what to show.
 */
import { LANE_COLUMNS, type LaneColumn } from '@viko/transcript-ui'
import type { Lane, LanesModel } from './lanes-model.js'

/** A lane with no agent on it and nothing queued, running or verifying (only landed work). */
export function isIdleLane(l: Lane): boolean {
  return (
    !l.agents.length &&
    !l.hot &&
    l.columns.queued.length + l.columns.running.length + l.columns.verifying.length === 0
  )
}

/** Active lanes stay on the board; idle ones fold into one `details` line under it. */
export function foldIdleLanes(lanes: readonly Lane[]): { active: Lane[]; idle: Lane[] } {
  const active: Lane[] = []
  const idle: Lane[] = []
  for (const l of lanes) (isIdleLane(l) ? idle : active).push(l)
  return { active, idle }
}

/** Cards per board column over the given lanes. */
export function columnTotals(lanes: readonly Lane[]): Record<LaneColumn, number> {
  const out = Object.fromEntries(LANE_COLUMNS.map((c) => [c.key, 0])) as Record<LaneColumn, number>
  for (const l of lanes) for (const c of LANE_COLUMNS) out[c.key] += l.columns[c.key].length
  return out
}

export type LanesHeadPart = { key: 'running' | 'queued' | 'landed'; n: number; label: string }

/** The Lanes head sentence ("2 running · 5 queued · 1 landed today"); zero parts are dropped. */
export function lanesHeadParts(counts: LanesModel['counts']): LanesHeadPart[] {
  const parts: LanesHeadPart[] = [
    { key: 'running', n: counts.running, label: 'running' },
    { key: 'queued', n: counts.queued, label: 'queued' },
    { key: 'landed', n: counts.landedToday, label: 'landed today' },
  ]
  return parts.filter((p) => p.n > 0)
}

/** Asks the freshness check flags as possibly outdated fold under one line ("2 may be outdated"). */
export function splitOutdated<T extends { outdated?: boolean | undefined }>(
  rows: readonly T[],
): { current: T[]; outdated: T[] } {
  const current: T[] = []
  const outdated: T[] = []
  for (const r of rows) (r.outdated ? outdated : current).push(r)
  return { current, outdated }
}
